import React, { useState, useEffect, useRef, useCallback } from "react";

// IMPORT PATH: assumes this file lives in src/pages/CompanyPages/ (same
// folder as CourseHubDashboard). If it lives somewhere else, change the
// number of "../" so it still reaches src/utils/courseCatalog.js.
import { TRACKS, getTrackCourses, ALL_COURSE_LABELS } from "../utils/courseCatalog";

// ============================================================================
// AI Course Assistance Page
//
// Description: Interactive guide helping users choose a tech profession,
// view their personalized learning roadmap, and directs them to signup.
//
// Design Updates:
// - Strictly restricted palette: Black, White, Gold, and Goldenrod.
// - Removed background particle field.
// - Height and spacing improvements for a better widescreen layout.
// - Reduced `.track-card` padding and adjusted internal elements to match
//   the new compact size.
//
// UPDATED (this pass):
// - Tracks and their courses are no longer written out by hand here — they
//   are built from utils/courseCatalog.js, the same catalog the Course Hub,
//   pricing page and signup form read. The roadmap a student sees here is
//   now exactly the set of courses they get in the Hub (previously this
//   page listed marketing course names such as "Next.js & SSR" that don't
//   exist as courses on the platform).
// - Added tracks: Embedded & IoT, System Design, Cloud, AI & LLM
//   (8 tracks total). Add or change a track in the catalog and it appears
//   here automatically.
// - Cards, blocks and text sized down throughout (profession cards,
//   roadmap nodes, summary blocks, headings, buttons, dialog padding).
//   Colors, animation and flow are unchanged.
// - Course levels/durations shown in the roadmap are ESTIMATES defined in
//   the catalog; adjust them there to the real course lengths.
// ============================================================================

// ─── Constants & Data ────────────────────────────────────────────────────────

const COLORS = {
    black: "#100720",
    dark: "#0a0a0a",
    cardBg: "#111111",
    white: "#FFFFFF",
    gold: "#FFD700",
    goldenrod: "#DAA520",
    goldenrodTrans: "rgba(218, 165, 32, 0.15)",
    goldenrodGlow: "rgba(218, 165, 32, 0.4)",
    whiteTrans: "rgba(255, 255, 255, 0.1)"
};

const LEVEL_ICONS = { Foundation: "◈", Core: "◉", Advanced: "◆", Mastery: "★" };

// Built from the shared catalog. Colors alternate gold / goldenrod as before.
const PROFESSIONS = TRACKS.map((track, i) => ({
    id: track.id,
    title: track.title,
    icon: track.icon || "◈",
    color: i % 2 === 0 ? COLORS.gold : COLORS.goldenrod,
    glow: COLORS.goldenrodGlow,
    tagline: track.tagline,
    courses: getTrackCourses(track.id).map((c) => ({
        name: c.label,
        level: c.level,
        duration: `${c.weeks} weeks`,
        icon: LEVEL_ICONS[c.level] || "◈",
    })),
}));

const LEVEL_COLORS = {
    Foundation: { bg: "rgba(255,255,255,0.05)", border: "rgba(255,255,255,0.2)", text: COLORS.white, dot: COLORS.white },
    Core:       { bg: "rgba(218,165,32,0.1)",   border: "rgba(218,165,32,0.3)",  text: COLORS.goldenrod, dot: COLORS.goldenrod },
    Advanced:   { bg: "rgba(255,215,0,0.1)",    border: "rgba(255,215,0,0.4)",   text: COLORS.gold, dot: COLORS.gold },
    Mastery:    { bg: "rgba(255,215,0,0.2)",    border: "rgba(255,215,0,0.6)",   text: COLORS.gold, dot: COLORS.white },
};

const totalWeeksOf = (courses) => courses.reduce((s, c) => s + parseInt(c.duration, 10), 0);
const countLabel = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;
const phaseCountOf = (courses) => new Set(courses.map((c) => c.level)).size;

// ─── Centralized Styles Object ───────────────────────────────────────────────

const STYLES = {
    pageContainer: {
        minHeight: "100vh",
        borderRadius:"25px",
        backgroundColor: COLORS.black,
        fontFamily: "'DM Mono', monospace",
        color: COLORS.white,
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        alignItems: "center"
    },
    heroSection: {
        position: "relative", zIndex: 1,
        padding: "clamp(48px, 8vw, 90px) 20px 48px",
        textAlign: "center", width: "100%", maxWidth: "1200px",
        display: "flex", flexDirection: "column", alignItems: "center",
        flex: 1, justifyContent: "center"
    },
    heroBackgroundGlow: {
        position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)",
        width: "80%", height: "80%",
        background: `radial-gradient(ellipse, rgba(218,165,32,0.1) 0%, transparent 70%)`,
        pointerEvents: "none"
    },
    heroBadge: {
        display: "inline-flex", alignItems: "center", gap: 8,
        background: COLORS.goldenrodTrans, border: `1px solid rgba(218,165,32,0.3)`,
        borderRadius: 30, padding: "6px 16px", marginBottom: 22,
        animation: "slideUp 0.6s ease 0.1s both"
    },
    stepBadge: {
        marginBottom: 6, display: "inline-flex", alignItems: "center", gap: 10,
        background: COLORS.goldenrodTrans, border: `1px solid rgba(218,165,32,0.3)`,
        borderRadius: 40, padding: "4px 14px"
    },
    heroText: {
        fontSize: "clamp(13px, 1.6vw, 15px)", color: "rgba(255,255,255,0.7)", maxWidth: 520,
        margin: "0 auto 40px", lineHeight: 1.7, animation: "slideUp 0.6s ease 0.3s both"
    },
    aiButtonWrapper: {
        position: "relative", display: "inline-flex", flexDirection: "column", alignItems: "center",
        gap: 16, cursor: "pointer", userSelect: "none", transition: "transform 0.4s ease"
    },
    aiButtonCore: {
        position: "relative", width: 160, height: 160
    },
    statsContainer: {
        display: "flex", justifyContent: "center", gap: "clamp(24px, 5vw, 56px)",
        flexWrap: "wrap", marginTop: 48, animation: "slideUp 0.6s ease 0.5s both"
    },
    trackGrid: {
        // Compact cards: 4 per row on wide screens (8 tracks = 2 rows)
        display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
        gap: 12, maxWidth: 1000, margin: "0 auto", width: "100%"
    },
    dialogOverlay: {
        position: "fixed", inset: 0, zIndex: 1000, display: "flex",
        alignItems: "center", justifyContent: "center", padding: "20px"
    },
    dialogBackdrop: {
        position: "absolute", inset: 0, background: "rgba(0,0,0,0.9)",
        backdropFilter: "blur(12px)", animation: "fadeIn 0.2s ease"
    },
    dialogContainer: {
        position: "relative", zIndex: 1, width: "100%", maxWidth: 600, maxHeight: "90vh", overflowY: "auto",
        backgroundColor: COLORS.cardBg, border: `1px solid ${COLORS.goldenrod}`, borderRadius: 20,
        boxShadow: `0 40px 100px rgba(0,0,0,0.9), 0 0 0 1px rgba(255,255,255,0.05) inset`,
        animation: "dialogSlideUp 0.4s ease", scrollbarWidth: "none",
        display: "flex", flexDirection: "column"
    },
    dialogHeader: {
        padding: "18px 24px 0", display: "flex", alignItems: "center", justifyContent: "space-between"
    },
    dialogContent: {
        padding: "18px 24px 26px", flex: 1
    },

    getProfessionCardStyle: (isHovered, p, i) => ({
        background: isHovered ? `linear-gradient(135deg, ${COLORS.goldenrodTrans}, ${COLORS.black})` : COLORS.cardBg,
        border: `1px solid ${isHovered ? p.color : COLORS.whiteTrans}`,
        borderRadius: 12, padding: "12px 14px", cursor: "pointer", textAlign: "left",
        transition: "all 0.3s ease",
        transform: isHovered ? "translateY(-3px) scale(1.02)" : "none",
        boxShadow: isHovered ? `0 10px 30px ${p.glow}` : "none",
        animationDelay: `${i * 0.06}s`, animation: "slideUp 0.5s ease both",
    }),
    getRoadmapNodeStyle: (isRevealed, isActive, lc, i) => ({
        maxWidth: 210, width: "100%",
        opacity: isRevealed ? 1 : 0,
        transform: isRevealed ? "none" : (i % 2 === 0 ? "translateX(-20px)" : "translateX(20px)"),
        transition: `all 0.4s ease ${i * 0.05}s`,
        background: isActive ? lc.bg : COLORS.cardBg,
        border: `1px solid ${isActive ? lc.border : COLORS.whiteTrans}`,
        borderRadius: 10, padding: "8px 12px", cursor: "pointer",
        textAlign: i % 2 === 0 ? "right" : "left",
        boxShadow: isActive ? `0 4px 20px ${lc.dot}44` : "none"
    })
};

// ─── Custom Hooks ────────────────────────────────────────────────────────────

/**
 * Text to Speech (TTS) Hook
 * Manages the browser's SpeechSynthesis API for accessible voice prompts.
 */
const useTTS = () => {
    const [speaking, setSpeaking] = useState(false);
    const [supported] = useState(() => "speechSynthesis" in window);

    const speak = useCallback((text) => {
        if (!supported) return;
        window.speechSynthesis.cancel();

        const utter = new SpeechSynthesisUtterance(text);
        utter.rate = 0.95;
        utter.pitch = 1.05;
        utter.volume = 1;

        const voices = window.speechSynthesis.getVoices();
        const preferred = voices.find(v => v.name.includes("Google") || v.name.includes("Samantha") || v.lang === "en-US");
        if (preferred) utter.voice = preferred;

        utter.onstart = () => setSpeaking(true);
        utter.onend = () => setSpeaking(false);
        utter.onerror = () => setSpeaking(false);

        window.speechSynthesis.speak(utter);
    }, [supported]);

    const stop = useCallback(() => {
        window.speechSynthesis.cancel();
        setSpeaking(false);
    }, []);

    return { speak, stop, speaking, supported };
};

// ─── Components ──────────────────────────────────────────────────────────────

/**
 * Step 1: Profession Selector
 */
const ProfessionStep = ({ onSelect, speak }) => {
    const [hovered, setHovered] = useState(null);

    useEffect(() => {
        speak("Welcome to the AI Course Advisor. Please select your desired career path from the options below.");
    }, [speak]);

    return (

        <div style={{ textAlign: "center",  }}>
            <div style={STYLES.stepBadge}>
                <span className="text-badge" style={{ color: COLORS.goldenrod }}>Step 01 of 03</span>
            </div>

            <h2 className="title-heading">
                Choose Your <span style={{ color: COLORS.gold }}>Career Path</span>
            </h2>
            <p className="subtitle-text">Select the profession you want to master</p>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10 }}>
                {PROFESSIONS.map((p, i) => (
                    <button
                        key={p.id}
                        onClick={() => onSelect(p)}
                        onMouseEnter={() => { setHovered(p.id); speak(p.title + ". " + p.tagline); }}
                        onMouseLeave={() => setHovered(null)}
                        style={STYLES.getProfessionCardStyle(hovered === p.id, p, i)}
                    >
                        <div style={{ fontSize: 22, color: p.color, marginBottom: 8, filter: `drop-shadow(0 0 6px ${p.color})` }}>
                            {p.icon}
                        </div>
                        <div className="card-title">{p.title}</div>
                        <div className="card-tagline">{p.tagline}</div>

                        <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 6 }}>
                            <span className="meta-text" style={{ color: p.color }}>{countLabel(p.courses.length, "course")}</span>
                            <span style={{ width: 1, height: 10, backgroundColor: COLORS.whiteTrans }} />
                            <span className="meta-text" style={{ color: "rgba(255,255,255,0.4)" }}>
                                ~{totalWeeksOf(p.courses)} weeks
                            </span>
                        </div>
                    </button>
                ))}
            </div>
        </div>

    );
};

/**
 * Step 2: Course Roadmap
 */
const RoadmapStep = ({ profession, onNext, speak }) => {
    const [revealed, setRevealed] = useState(0);
    const [activeNode, setActiveNode] = useState(null);
    const phases = phaseCountOf(profession.courses);

    useEffect(() => {
        speak(`Excellent choice! Here is your personalised roadmap for ${profession.title}. You'll journey through ${countLabel(profession.courses.length, "carefully curated course")}.`);

        const timer = setInterval(() => {
            setRevealed(r => {
                if (r >= profession.courses.length) {
                    clearInterval(timer);
                    return r;
                }
                return r + 1;
            });
        }, 220);

        return () => clearInterval(timer);
    }, [profession, speak]);

    return (
        <div>
            <div style={{ textAlign: "center", marginBottom: 18 }}>
                <div style={STYLES.stepBadge}>
                    <span className="text-badge" style={{ color: COLORS.goldenrod }}>Step 02 of 03</span>
                </div>
                <h2 className="title-heading" style={{ fontSize: "clamp(17px,2.6vw,22px)" }}>
                    Your <span style={{ color: profession.color, textShadow: `0 0 20px ${profession.color}` }}>{profession.title}</span> Roadmap
                </h2>
                <p className="subtitle-text" style={{ marginBottom: 0 }}>
                    AI-curated learning pathway — {countLabel(profession.courses.length, "course")} across {countLabel(phases, "phase")}
                </p>
            </div>

            {/* Legend */}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center", marginBottom: 18 }}>
                {Object.entries(LEVEL_COLORS).map(([lvl, c]) => (
                    <div key={lvl} style={{ display: "flex", alignItems: "center", gap: 6, backgroundColor: c.bg, border: `1px solid ${c.border}`, borderRadius: 20, padding: "3px 9px" }}>
                        <div style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: c.dot }} />
                        <span className="meta-text" style={{ color: c.text }}>{lvl}</span>
                    </div>
                ))}
            </div>

            {/* Timeline */}
            <div style={{ display: "flex", flexDirection: "column", position: "relative" }}>
                <div style={{
                    position: "absolute", left: "50%", top: 16, bottom: 16, width: 2,
                    background: `linear-gradient(to bottom, ${profession.color}44, ${profession.color}11)`,
                    transform: "translateX(-50%)", zIndex: 0
                }} />

                {profession.courses.map((course, i) => {
                    const lc = LEVEL_COLORS[course.level];
                    const isLeft = i % 2 === 0;
                    const isRevealed = i < revealed;
                    const isActive = activeNode === i;

                    return (
                        <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "center", position: "relative", zIndex: 1, minHeight: 54 }}>
                            {/* Left Side */}
                            {isLeft ? (
                                <div style={{ flex: 1, paddingRight: 16, display: "flex", justifyContent: "flex-end" }}>
                                    <button
                                        onClick={() => { setActiveNode(isActive ? null : i); speak(`${course.name}. Level: ${course.level}. Duration: ${course.duration}`); }}
                                        style={STYLES.getRoadmapNodeStyle(isRevealed, isActive, lc, i)}
                                    >
                                        <div className="card-title" style={{ fontSize: 12, marginBottom: 2, color: isActive ? lc.text : COLORS.white }}>{course.name}</div>
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 6 }}>
                                            <span className="meta-text" style={{ color: lc.text }}>{course.duration}</span>
                                            <div style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: lc.dot }} />
                                        </div>
                                    </button>
                                </div>
                            ) : <div style={{ flex: 1 }} />}

                            {/* Center Node */}
                            <div style={{
                                width: 30, height: 30, flexShrink: 0, borderRadius: "50%",
                                background: isRevealed ? `radial-gradient(circle, ${profession.color}33, transparent)` : "transparent",
                                border: `2px solid ${isRevealed ? profession.color : COLORS.whiteTrans}`,
                                display: "flex", alignItems: "center", justifyContent: "center",
                                boxShadow: isRevealed ? `0 0 12px ${profession.glow}` : "none",
                                transition: `all 0.4s ease ${i * 0.05}s`, fontSize: 13, color: profession.color
                            }}>
                                {isRevealed ? <span style={{ animation: "pulse 2s ease infinite" }}>{course.icon}</span> : null}
                            </div>

                            {/* Right Side */}
                            {!isLeft ? (
                                <div style={{ flex: 1, paddingLeft: 16 }}>
                                    <button
                                        onClick={() => { setActiveNode(isActive ? null : i); speak(`${course.name}. Level: ${course.level}. Duration: ${course.duration}`); }}
                                        style={STYLES.getRoadmapNodeStyle(isRevealed, isActive, lc, i)}
                                    >
                                        <div className="card-title" style={{ fontSize: 12, marginBottom: 2, color: isActive ? lc.text : COLORS.white }}>{course.name}</div>
                                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                            <div style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: lc.dot }} />
                                            <span className="meta-text" style={{ color: lc.text }}>{course.duration}</span>
                                        </div>
                                    </button>
                                </div>
                            ) : <div style={{ flex: 1 }} />}
                        </div>
                    );
                })}
            </div>

            <button
                onClick={() => { speak("All these courses are available in our platform. Let's move to registration."); onNext(); }}
                className="cta-button"
                style={{
                    marginTop: 18,
                    background: `linear-gradient(135deg, ${COLORS.gold}, ${COLORS.goldenrod})`,
                    boxShadow: `0 8px 30px rgba(218, 165, 32, 0.3)`
                }}
            >
                ✦ Our Platform Covers All These — Apply Now
            </button>
        </div>
    );
};

/**
 * Step 3: Application Step
 */
const ApplicationStep = ({ profession, onClose, speak }) => {
    const [activeTab, setActiveTab] = useState("overview");
    const [redirecting, setRedirecting] = useState(false);
    const totalWeeks = totalWeeksOf(profession.courses);
    const phases = phaseCountOf(profession.courses);

    useEffect(() => {
        speak(`Fantastic! Our platform fully covers all ${profession.title} courses. Review your track below, then create your account to get started.`);
    }, [profession, speak]);

    const handleSignUp = () => {
        speak(`Taking you to registration now. Welcome to the ${profession.title} track!`);
        setRedirecting(true);
        const params = new URLSearchParams({ track: profession.id, title: profession.title });

        setTimeout(() => {
            window.location.href = `/signup?${params.toString()}`;
        }, 900);
    };

    return (
        <div>
            {/* Header */}
            <div style={{ textAlign: "center", marginBottom: 16 }}>
                <div style={STYLES.stepBadge}>
                    <span className="text-badge" style={{ color: COLORS.goldenrod }}>Step 03 of 03</span>
                </div>
                <h2 className="title-heading" style={{ fontSize: "clamp(16px,2.4vw,20px)" }}>
                    ✦ Our Platform <span style={{ color: profession.color }}>Covers It All</span>
                </h2>
                <p className="subtitle-text" style={{ marginBottom: 0 }}>Review your track, then create your free account</p>
            </div>

            {/* Track Summary */}
            <div style={{
                display: "flex", alignItems: "center", gap: 12, backgroundColor: COLORS.cardBg,
                border: `1px solid ${COLORS.goldenrodTrans}`, borderRadius: 12, padding: "10px 14px", marginBottom: 14
            }}>
                <div style={{ fontSize: 28, color: profession.color, filter: `drop-shadow(0 0 8px ${profession.color})`, flexShrink: 0 }}>
                    {profession.icon}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="card-title" style={{ fontSize: 14, marginBottom: 2 }}>{profession.title}</div>
                    <div className="meta-text" style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", lineHeight: 1.5 }}>
                        {countLabel(profession.courses.length, "course")} · ~{totalWeeks} weeks · {countLabel(phases, "phase")}
                    </div>
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div className="card-title" style={{ fontSize: 20, marginBottom: 0, color: profession.color }}>{totalWeeks}</div>
                    <div className="meta-text" style={{ fontSize: 10, color: "rgba(255,255,255,0.4)" }}>weeks</div>
                </div>
            </div>

            {/* Tab Switcher */}
            <div style={{
                display: "flex", gap: 0, marginBottom: 14, backgroundColor: COLORS.black,
                border: `1px solid ${COLORS.whiteTrans}`, borderRadius: 10, padding: 3
            }}>
                {[["overview", "⚡ Overview"], ["courses", "📚 All Courses"]].map(([key, label]) => (
                    <button
                        key={key}
                        onClick={() => setActiveTab(key)}
                        className="tab-button"
                        style={{
                            backgroundColor: activeTab === key ? COLORS.goldenrod : "transparent",
                            color: activeTab === key ? COLORS.black : COLORS.white,
                            fontWeight: activeTab === key ? 700 : 400,
                        }}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {/* Tab Content: Overview */}
            {activeTab === "overview" && (
                <div style={{ animation: "slideUp 0.3s ease" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 }}>
                        {[
                            { icon: "🎯", label: "Project-based", sub: "Real-world projects" },
                            { icon: "🤖", label: "AI Mentoring", sub: "24/7 AI support" },
                            { icon: "📜", label: "Certificate", sub: "Industry recognised" },
                            { icon: "💼", label: "Job Ready", sub: "Career placement help" },
                        ].map(({ icon, label, sub }) => (
                            <div key={label} style={{
                                backgroundColor: COLORS.cardBg, border: `1px solid ${COLORS.whiteTrans}`,
                                borderRadius: 10, padding: "8px 10px", display: "flex", alignItems: "center", gap: 10
                            }}>
                                <span style={{ fontSize: 18 }}>{icon}</span>
                                <div>
                                    <div className="card-title" style={{ fontSize: 11, marginBottom: 0 }}>{label}</div>
                                    <div className="meta-text" style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", marginTop: 1 }}>{sub}</div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "center", marginBottom: 14 }}>
                        {profession.courses.map((c, i) => (
                            <div key={i} className="meta-text" style={{
                                backgroundColor: COLORS.goldenrodTrans, border: `1px solid rgba(218,165,32,0.3)`,
                                borderRadius: 20, padding: "4px 9px", fontSize: 10, color: COLORS.gold,
                                display: "flex", alignItems: "center", gap: 5
                            }}>
                                <span style={{ color: COLORS.white }}>✓</span> {c.name}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Tab Content: Courses */}
            {activeTab === "courses" && (
                <div style={{ animation: "slideUp 0.3s ease" }}>
                    {Object.entries(
                        profession.courses.reduce((acc, c) => {
                            if (!acc[c.level]) acc[c.level] = [];
                            acc[c.level].push(c);
                            return acc;
                        }, {})
                    ).map(([level, courses]) => {
                        const lc = LEVEL_COLORS[level];
                        const phaseWeeks = totalWeeksOf(courses);

                        return (
                            <div key={level} style={{ marginBottom: 8, backgroundColor: lc.bg, border: `1px solid ${lc.border}`, borderRadius: 10, padding: "8px 12px" }}>
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        <div style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: lc.dot }} />
                                        <span className="card-title" style={{ fontSize: 12, color: lc.text, margin: 0 }}>{level} Phase</span>
                                    </div>
                                    <span className="meta-text" style={{
                                        fontSize: 10, color: lc.text, backgroundColor: COLORS.black,
                                        border: `1px solid ${lc.border}`, borderRadius: 20, padding: "3px 8px"
                                    }}>
                                        {phaseWeeks}w · {countLabel(courses.length, "course")}
                                    </span>
                                </div>
                                <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                                    {courses.map((c, i) => (
                                        <div key={i} style={{
                                            display: "flex", alignItems: "center", justifyContent: "space-between",
                                            backgroundColor: COLORS.cardBg, borderRadius: 8, padding: "6px 10px"
                                        }}>
                                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                <span style={{ color: lc.dot, fontSize: 12 }}>{c.icon}</span>
                                                <span className="card-title" style={{ fontSize: 11, margin: 0 }}>{c.name}</span>
                                            </div>
                                            <span className="meta-text" style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", flexShrink: 0, marginLeft: 8 }}>{c.duration}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Action Buttons */}
            <button
                onClick={handleSignUp}
                disabled={redirecting}
                className="cta-button"
                style={{
                    backgroundColor: redirecting ? COLORS.cardBg : COLORS.gold,
                    color: redirecting ? "rgba(255,255,255,0.4)" : COLORS.black,
                    cursor: redirecting ? "default" : "pointer",
                    boxShadow: redirecting ? "none" : `0 8px 30px rgba(218, 165, 32, 0.3)`,
                    marginTop: 16
                }}
            >
                {redirecting ? (
                    <>
                        <span style={{
                            display: "inline-block", width: 16, height: 16, border: `2px solid ${COLORS.whiteTrans}`,
                            borderTopColor: COLORS.white, borderRadius: "50%", animation: "spin 0.7s linear infinite"
                        }} />
                        Redirecting to Sign Up…
                    </>
                ) : "✦ Create Account & Get Started"}
            </button>

            <p className="meta-text" style={{ textAlign: "center", marginTop: 12, fontSize: 11, color: "rgba(255,255,255,0.4)" }}>
                Already registered?{" "}
                <a href="/signin" style={{ color: COLORS.gold, textDecoration: "none", fontWeight: 600 }}>Sign in here</a>
            </p>
        </div>
    );
};

/**
 * Main Advisor Dialog Modal
 */
const AdvisorDialog = ({ open, onClose, speak, stop, speaking }) => {
    const [step, setStep] = useState(1);
    const [profession, setProfession] = useState(null);
    const dialogRef = useRef(null);

    useEffect(() => {
        if (!open) {
            setStep(1);
            setProfession(null);
            stop();
        }
    }, [open, stop]);

    useEffect(() => {
        if (open && dialogRef.current) dialogRef.current.scrollTop = 0;
    }, [step, open]);

    if (!open) return null;

    return (
        <div style={STYLES.dialogOverlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div style={STYLES.dialogBackdrop} />
            <div ref={dialogRef} style={STYLES.dialogContainer}>

                {/* Header line decoration */}
                <div style={{ position: "absolute", top: 0, left: "10%", right: "10%", height: 2, background: `linear-gradient(to right, transparent, ${COLORS.goldenrod}, transparent)` }} />

                <div style={STYLES.dialogHeader}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{
                            width: 32, height: 32, borderRadius: "50%",
                            background: `radial-gradient(circle, ${COLORS.goldenrodTrans}, transparent)`,
                            border: `1px solid ${COLORS.goldenrod}`, display: "flex", alignItems: "center", justifyContent: "center",
                            animation: "pulse 2s ease infinite", boxShadow: `0 0 20px ${COLORS.goldenrodTrans}`
                        }}>
                            <span style={{ fontSize: 15, color: COLORS.gold }}>◈</span>
                        </div>
                        <div>
                            <div className="card-title" style={{ fontSize: 14, margin: 0 }}>AI Course Advisor</div>
                            <div className="meta-text" style={{ fontSize: 10, color: COLORS.goldenrod, marginTop: 3 }}>
                                {speaking ? "● Speaking..." : "● Ready"}
                            </div>
                        </div>
                    </div>

                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <button
                            onClick={speaking ? stop : () => speak("AI Course Advisor is active and ready to assist you.")}
                            className="icon-button"
                            style={{
                                border: `1px solid ${speaking ? COLORS.gold : COLORS.whiteTrans}`,
                                backgroundColor: speaking ? COLORS.goldenrodTrans : COLORS.cardBg,
                                color: speaking ? COLORS.gold : COLORS.white
                            }}
                        >
                            {speaking ? "⏹" : "🔊"}
                        </button>
                        <button onClick={onClose} className="icon-button close-btn">✕</button>
                    </div>
                </div>

                <div style={{ padding: "12px 24px 0", display: "flex", gap: 8, alignItems: "center" }}>
                    {[1,2,3].map(s => (
                        <div key={s} style={{
                            height: 4, flex: 1, borderRadius: 2,
                            backgroundColor: s <= step ? COLORS.gold : COLORS.whiteTrans,
                            transition: "all 0.4s ease",
                            boxShadow: s === step ? `0 0 8px ${COLORS.goldenrod}` : "none"
                        }} />
                    ))}
                </div>

                <div style={STYLES.dialogContent}>
                    {step === 1 && <ProfessionStep onSelect={(p) => { setProfession(p); setStep(2); }} speak={speak} />}
                    {step === 2 && profession && <RoadmapStep profession={profession} onNext={() => setStep(3)} speak={speak} />}
                    {step === 3 && profession && <ApplicationStep profession={profession} onClose={onClose} speak={speak} />}
                </div>
            </div>
        </div>
    );
};

// ─── Main Page Entry Component ───────────────────────────────────────────────

const AiCourseAssistancePage = () => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [aiHovered, setAiHovered] = useState(false);
    const { speak, stop, speaking, supported } = useTTS();

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;700;800&family=DM+Mono:wght@400;500&display=swap');

                @keyframes slideUp { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
                @keyframes fadeIn  { from { opacity:0; } to { opacity:1; } }
                @keyframes pulse   { 0%,100% { opacity:1; transform:scale(1); } 50% { opacity:0.8; transform:scale(0.97); } }
                @keyframes spin    { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }
                @keyframes float   { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-10px); } }
                @keyframes glow    { 0%,100% { opacity:0.6; } 50% { opacity:1; } }
                @keyframes dialogSlideUp { from { opacity:0; transform:translateY(30px) scale(0.96); } to { opacity:1; transform:none; } }

                ::-webkit-scrollbar { display:none; }

                /* Typography Utilities (sized down this pass) */
                .text-badge { font-size: 11px; font-weight: 600; letter-spacing: 2px; font-family: 'DM Mono', monospace; text-transform: uppercase; }
                .title-heading { font-family: 'Syne', sans-serif; font-size: clamp(20px, 3vw, 30px); font-weight: 800; color: #fff; margin: 10px 0 8px; line-height: 1.2; }
                .subtitle-text { color: rgba(255,255,255,0.6); font-size: 13px; margin-bottom: 24px; font-family: 'DM Mono', monospace; }
                .card-title { font-family: 'Syne', sans-serif; font-size: 14px; font-weight: 700; color: #fff; margin-bottom: 4px; }
                .card-tagline { font-family: 'DM Mono', monospace; font-size: 11px; color: rgba(255,255,255,0.5); line-height: 1.4; }
                .meta-text { font-family: 'DM Mono', monospace; font-size: 11px; }

                /* Buttons */
                .cta-button { width: 100%; padding: 13px 20px; border-radius: 12px; border: none; font-family: 'Syne', sans-serif; font-weight: 800; font-size: 13px; cursor: pointer; transition: all 0.3s; letter-spacing: 1px; display: flex; align-items: center; justify-content: center; gap: 10px; }
                .cta-button:hover:not(:disabled) { transform: translateY(-3px) scale(1.02); }

                .tab-button { flex: 1; padding: 9px 0; border-radius: 8px; border: none; cursor: pointer; font-family: 'DM Mono', monospace; font-size: 11px; letter-spacing: 1px; transition: all 0.25s; }
                .icon-button { width: 30px; height: 30px; border-radius: 50%; cursor: pointer; font-size: 14px; display: flex; align-items: center; justify-content: center; transition: all 0.2s; }
                .close-btn { border: 1px solid rgba(255,255,255,0.2); background: rgba(255,255,255,0.05); color: rgba(255,255,255,0.6); }
                .close-btn:hover { border-color: ${COLORS.gold}; color: ${COLORS.gold}; }

                /* Track Card - compact */
                .track-card { background-color: ${COLORS.cardBg}; border: 1px solid rgba(255,255,255,0.1); border-radius: 14px; padding: 10px 12px; cursor: pointer; transition: all 0.3s; }
                .track-card:hover { transform: translateY(-4px); }
            `}</style>

            <div style={STYLES.pageContainer}>

                {/* Main Hero Section */}
                <section style={STYLES.heroSection}>
                    <div style={STYLES.heroBackgroundGlow} />

                    <div style={STYLES.heroBadge}>
                        <div style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: COLORS.gold, animation: "pulse 2s ease infinite" }} />
                        <span className="text-badge" style={{ color: COLORS.gold }}>AI-Powered Course Advisor</span>
                    </div>

                    <p style={STYLES.heroText}>
                        Let our AI advisor map your personalised learning path — from zero to job-ready in weeks, not years.
                    </p>

                    {/* Interactive AI Core Button */}
                    <div style={{ animation: "slideUp 0.6s ease 0.4s both", display: "inline-block" }}>
                        <div
                            onClick={() => { setDialogOpen(true); speak("Opening AI Course Advisor. Get ready for your personalised learning journey!"); }}
                            onMouseEnter={() => setAiHovered(true)}
                            onMouseLeave={() => setAiHovered(false)}
                            style={{
                                ...STYLES.aiButtonWrapper,
                                transform: aiHovered ? "scale(1.05)" : "scale(1)"
                            }}
                        >
                            <div style={STYLES.aiButtonCore}>
                                {/* Animated Rings limited strictly to Gold/Goldenrod palette */}
                                <div style={{ position: "absolute", inset: -8, borderRadius: "50%", border: `1.5px dashed ${COLORS.goldenrodTrans}`, animation: "spin 12s linear infinite" }} />
                                <div style={{ position: "absolute", inset: -16, borderRadius: "50%", border: `1px dashed rgba(255, 215, 0, 0.2)`, animation: "spin 18s linear infinite reverse" }} />

                                <div style={{
                                    position: "absolute", inset: 0, borderRadius: "50%",
                                    background: `radial-gradient(circle, ${COLORS.goldenrodTrans} 0%, transparent 70%)`,
                                    animation: aiHovered ? "glow 1.5s ease infinite" : "glow 3s ease infinite"
                                }} />

                                <div style={{
                                    position: "absolute", inset: 0, borderRadius: "50%",
                                    background: aiHovered ? `linear-gradient(135deg, ${COLORS.goldenrodTrans}, rgba(255,215,0,0.1))` : `linear-gradient(135deg, rgba(218,165,32,0.05), rgba(255,215,0,0.05))`,
                                    border: `2px solid ${aiHovered ? COLORS.gold : COLORS.goldenrod}`,
                                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4,
                                    boxShadow: aiHovered ? `0 0 60px ${COLORS.goldenrodGlow}` : `0 0 30px rgba(218,165,32,0.15)`,
                                    transition: "all 0.3s", animation: "float 4s ease infinite"
                                }}>
                                    <span style={{ fontSize: 48, color: COLORS.gold, filter: `drop-shadow(0 0 12px ${COLORS.gold})` }}>◈</span>
                                    <span className="text-badge" style={{ fontSize: 10, color: COLORS.white }}>AI</span>
                                </div>

                                {/* Orbital Dots */}
                                {[0, 1, 2].map(i => (
                                    <div key={i} style={{
                                        position: "absolute", width: 10, height: 10, borderRadius: "50%",
                                        backgroundColor: COLORS.gold, boxShadow: `0 0 10px ${COLORS.gold}`,
                                        top: "50%", left: "50%", transform: `rotate(${i * 120}deg) translateX(72px) translateY(-50%)`,
                                        animation: `spin ${8 + i * 2}s linear infinite`
                                    }} />
                                ))}
                            </div>

                            <div style={{ textAlign: "center", marginTop: 8 }}>
                                <div className="card-title" style={{ fontSize: 16, color: COLORS.gold, marginBottom: 4 }}>
                                    Course Advice
                                </div>
                                <div className="meta-text" style={{ fontSize: 12, color: "rgba(255,255,255,0.5)" }}>
                                    Click to get AI guidance
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Quick Stats Outline — counts come from the catalog */}
                    <div style={STYLES.statsContainer}>
                        {[[String(PROFESSIONS.length), "Career Paths"], [String(ALL_COURSE_LABELS.length), "Courses"], ["100%", "Coverage"], ["24h", "Response"]].map(([n, l]) => (
                            <div key={l} style={{ textAlign: "center" }}>
                                <div style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: "clamp(20px, 3vw, 28px)", color: COLORS.gold, lineHeight: 1 }}>{n}</div>
                                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", marginTop: 6, letterSpacing: 1 }}>{l}</div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Previews */}
                <section style={{ position: "relative", zIndex: 1, padding: "0 20px 60px", width: "100%" }}>
                    <div style={{ textAlign: "center", marginBottom: 18 }}>
                        <p style={{ color: COLORS.goldenrod, fontSize: 13 }}>Explore standard tracks below or click the AI advisor for mapping</p>
                    </div>

                    <div style={STYLES.trackGrid}>
                        {PROFESSIONS.map((p, i) => (
                            <div
                                key={p.id}
                                onClick={() => setDialogOpen(true)}
                                className="track-card"
                                style={{ animation: `slideUp 0.5s ease ${0.1 + i * 0.06}s both` }}
                                onMouseEnter={e => { e.currentTarget.style.borderColor = p.color; e.currentTarget.style.backgroundColor = COLORS.dark; }}
                                onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; e.currentTarget.style.backgroundColor = COLORS.cardBg; }}
                            >
                                <div style={{ fontSize: 22, color: p.color, marginBottom: 8, filter: `drop-shadow(0 0 8px ${p.color})` }}>{p.icon}</div>
                                <h3 className="card-title" style={{ fontSize: 14 }}>{p.title}</h3>
                                <p style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", margin: "0 0 10px", lineHeight: 1.4 }}>{p.tagline}</p>

                                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                                    <span className="meta-text" style={{
                                        color: COLORS.black, backgroundColor: p.color,
                                        borderRadius: 20, padding: "3px 8px", fontWeight: "bold"
                                    }}>
                                        {countLabel(p.courses.length, "course")}
                                    </span>
                                    <span className="meta-text" style={{
                                        color: COLORS.white, backgroundColor: COLORS.cardBg,
                                        border: `1px solid ${COLORS.whiteTrans}`, borderRadius: 20, padding: "3px 8px"
                                    }}>
                                        ~{totalWeeksOf(p.courses)}w
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* TTS Status Widget */}
                {supported && (
                    <div style={{
                        position: "fixed", bottom: 24, right: 24, zIndex: 100, display: "flex", alignItems: "center", gap: 12,
                        backgroundColor: COLORS.cardBg, border: `1px solid ${speaking ? COLORS.gold : COLORS.whiteTrans}`,
                        borderRadius: 30, padding: "8px 14px", boxShadow: speaking ? `0 4px 20px ${COLORS.goldenrodTrans}` : "none",
                        transition: "all 0.3s"
                    }}>
                        <div style={{
                            width: 10, height: 10, borderRadius: "50%", backgroundColor: speaking ? COLORS.gold : COLORS.whiteTrans,
                            animation: speaking ? "pulse 1s ease infinite" : "none", boxShadow: speaking ? `0 0 10px ${COLORS.gold}` : "none"
                        }} />
                        <span className="meta-text" style={{ color: speaking ? COLORS.gold : "rgba(255,255,255,0.5)", letterSpacing: 1, fontSize: 11 }}>
                            {speaking ? "AI Speaking..." : "Voice Ready"}
                        </span>
                        {speaking && (
                            <button onClick={stop} style={{ background: "none", border: "none", color: COLORS.white, cursor: "pointer", padding: 0, fontSize: 14 }}>
                                ⏹
                            </button>
                        )}
                    </div>
                )}

                {/* Main Dialog Modal Component */}
                <AdvisorDialog
                    open={dialogOpen}
                    onClose={() => { setDialogOpen(false); stop(); }}
                    speak={speak}
                    stop={stop}
                    speaking={speaking}
                />
            </div>
        </>
    );
};

export default AiCourseAssistancePage;
